// src/pages/fleet/TripDetailPage.tsx — full trip profile (layout follows the RFCL VLTS
// "Trips Details" page): details + live route map, distance / time analysis,
// activity logs, vehicle (GPS) logs and IFMS API logs with re-send.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  getTripDetails, sendIfms, startTrip, completeTrip, cancelTrip, updateTrip,
  addTripDealer, updateTripDealer, deleteTripDealer,
} from '../../services/fleetApi';
import type { IfmsApi, IfmsLogRow, LatLngTuple, TripActivityRow, TripDealerRow, TripDetails } from '../../services/fleetApi';
import type { Trip, TripLocation, TripStatus } from '../../types';
import type { Layer, Marker } from 'leaflet';
import { P, css } from './fleetStyles';
import Icon, { IconName } from './Icon';
import useIsMobile from './useIsMobile';
import { createFleetMap, zoomTo, isPlant, plantMarker, VEHICLE_ZOOM } from './mapKit';
import type { FleetMap } from './mapKit';
import { Modal, RCol, ReportTable, Tag, fmtDT, fmtDur, humanize } from './reports/reportKit';
import { apiError } from '../../utils/errors';
import { useAuth } from '../../store/AuthContext';
import { can } from './permissions';
import { tripBadge } from './tripBadge';
import { providerName } from './gpsProvider';
import TripTimeline from './TripTimeline';

const STATUS: Record<TripStatus, { label: string; bg: string }> = {
  PENDING: { label: 'Pending', bg: '#FF9500' },
  ACTIVE: { label: 'On Going', bg: '#E0525D' },
  COMPLETED: { label: 'Completed', bg: '#34C759' },
  CANCELLED: { label: 'Cancelled', bg: '#8E8E93' },
};
// Map colours: the planned route sits underneath as a dashed grey-blue line, the
// travelled trail is green on a white casing, and any stretch driven off the
// planned route is drawn on top in red so a deviation cannot be missed.
const PLANNED = '#6E7B8B';
const TRAVELLED = '#1DB954';
const OFF_ROUTE = '#FF3B30';
const CASING = '#FFFFFF';

const kmText = (v: number | null | undefined) => (v == null ? '—' : `${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })} KM`);
const signedDur = (m: number | null | undefined) => (m == null ? '—' : m === 0 ? '0m' : `${m > 0 ? '+' : '−'}${fmtDur(Math.abs(m))}`);
const dateOnly = (v: string | null | undefined) => (v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const timeOnly = (v: string | null | undefined) => (v ? new Date(v).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '');

function km(a: number, b: number, c: number, d: number) {
  const r = (x: number) => (x * Math.PI) / 180;
  const h = Math.sin(r(c - a) / 2) ** 2 + Math.cos(r(a)) * Math.cos(r(c)) * Math.sin(r(d - b) / 2) ** 2;
  return 6371 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}
// Progress = how much closer to the destination the truck is (0 at pickup, 100 at drop).
// `at` overrides the point it is measured from: during a replay that is the position
// the replay has reached, so the bar walks the route with the truck instead of
// sitting at the trip's final figure.
function progressPct(trip: Trip, locs: TripLocation[], at?: TripLocation | null) {
  const c = at || locs[locs.length - 1];
  if (!at && trip.status === 'COMPLETED') return 100;
  const total = km(trip.fromLat, trip.fromLng, trip.toLat, trip.toLng);
  const left = c ? km(c.latitude, c.longitude, trip.toLat, trip.toLng) : total;
  return Math.min(100, Math.max(0, Math.round(((total - left) / (total || 1)) * 100)));
}

export default function TripDetailPage() {
  const { id = '' } = useParams<'id'>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const canEdit = can(user, 'create_trip');
  const canAct = can(user, 'trip_list');

  const [data, setData] = useState<TripDetails | null>(null);
  const [loadErr, setLoadErr] = useState('');
  const [busy, setBusy] = useState('');
  const [notice, setNotice] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  // Map layers the user can switch off to declutter.
  const [showPlanned, setShowPlanned] = useState(true);
  const [showHalts, setShowHalts] = useState(true);
  const [showDeviations, setShowDeviations] = useState(true);
  // Replay: a small truck drives the recorded trail from start to finish.
  const [playing, setPlaying] = useState(false);
  const [replayAt, setReplayAt] = useState<TripLocation | null>(null);
  const replayMk = useRef<Marker | null>(null);
  const replayTimer = useRef<ReturnType<typeof setInterval> | null>(null);

  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<FleetMap | null>(null);
  const layers = useRef<Layer[]>([]);
  const fitted = useRef(false);
  // A running trip follows its truck. Switched off the moment the operator pans
  // the map themselves, so following never fights them, and switched back on by
  // the Show Truck button.
  const follow = useRef(true);
  const currentId = useRef(id);
  currentId.current = id;

  const load = useCallback(() => {
    const reqId = id;
    return getTripDetails(reqId)
      .then((r) => { if (currentId.current === reqId) { setData(r.data); setLoadErr(''); } })
      .catch((e) => { if (currentId.current === reqId) setLoadErr(apiError(e, 'Could not load this trip')); });
  }, [id]);

  // New trip id → start clean (map included).
  useEffect(() => {
    setData(null); setLoadErr(''); setNotice(null); fitted.current = false; follow.current = true; layers.current = [];
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    load();
  }, [id, load]);

  // Live refresh while the trip is running.
  const status = data?.trip.status;
  useEffect(() => {
    if (status !== 'ACTIVE') return;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [status, load]);

  useEffect(() => () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } }, []);

  // Map: planned road (orange), travelled GPS trail (green), S / E pins, stops, truck.
  useEffect(() => {
    if (!data || !window.L || !mapEl.current) return;
    if (!mapRef.current) {
      mapRef.current = createFleetMap(mapEl.current, [data.trip.fromLat, data.trip.fromLng], 11);
      // Only a drag — the pan pad and our own flyTo do not fire this, so moving
      // the map for the operator does not count as the operator moving it.
      mapRef.current.on('dragstart', () => { follow.current = false; });
      setTimeout(() => mapRef.current?.invalidateSize(), 300);
    }
    const map = mapRef.current, L = window.L, { trip, locations, roadPath, stops, deviations } = data;
    layers.current.forEach((l) => map.removeLayer(l)); layers.current = [];
    const add = <T extends Layer>(l: T): T => { layers.current.push(l.addTo(map)); return l; };

    // 1 — planned road route, underneath everything
    const planned: LatLngTuple[] = roadPath || [[trip.fromLat, trip.fromLng], [trip.toLat, trip.toLng]];
    if (showPlanned) {
      add(L.polyline(planned, { color: CASING, weight: 9, opacity: 0.9 }));
      add(L.polyline(planned, { color: PLANNED, weight: 4, opacity: 0.95, dashArray: '1 9', lineCap: 'round' }));
    }

    // 2 — where the truck actually went
    const trail = locations.map((l) => [l.latitude, l.longitude] as LatLngTuple);
    if (trail.length > 1) {
      add(L.polyline(trail, { color: CASING, weight: 10, opacity: 0.95 }));
      add(L.polyline(trail, { color: TRAVELLED, weight: 5, opacity: 1, lineCap: 'round', lineJoin: 'round' }));
    }

    // 3 — deviations on top, in red, each one labelled
    if (showDeviations) {
      deviations.forEach((d) => {
        add(L.polyline(d.points, { color: CASING, weight: 11, opacity: 0.95 }));
        add(L.polyline(d.points, { color: OFF_ROUTE, weight: 5.5, opacity: 1, lineCap: 'round' }));
        add(L.marker([d.latitude, d.longitude], {
          icon: L.divIcon({ className: 'fx-dev', html: `<div class="fx-dev-b">⚠ ${d.km} km off route</div>`, iconSize: [0, 0] }),
        }).bindPopup(
          `<b>Route deviation ${d.index}</b><br/>${d.km} km driven off the planned route<br/>`
          + `Farthest ${d.maxKm} km away · ${fmtDur(d.minutes)}<br/>${fmtDT(d.startAt)} → ${timeOnly(d.endAt)}`,
        ));
      });
    }

    // 4 — start / end / halts
    const pin = (lat: number, lng: number, letter: string, color: string, label: string): Marker => L.marker([lat, lng], {
      icon: L.divIcon({ className: 'fx-pin', html: `<div class="fx-pin-b" style="background:${color}"><span>${letter}</span></div>`, iconSize: [30, 38], iconAnchor: [15, 38], popupAnchor: [0, -34] }),
    }).bindPopup(label);
    // Trips start at the RFCL plant — show the factory badge there instead of a plain pin.
    add(isPlant(trip.fromLat, trip.fromLng)
      ? plantMarker({ lat: trip.fromLat, lng: trip.fromLng })
      : pin(trip.fromLat, trip.fromLng, 'S', '#1DB954', `<b>Start</b><br/>${escapeHtml(trip.fromLocation)}`));
    add(pin(trip.toLat, trip.toLng, 'E', '#FF3B30', `<b>Destination</b><br/>${escapeHtml(trip.toLocation)}`));
    if (showHalts) {
      stops.forEach((st, i) => add(pin(st.latitude, st.longitude, String(i + 1), st.unauthorized ? '#AF52DE' : '#FF9500',
        `<b>${st.unauthorized ? 'Unauthorized halt' : 'Halt'} ${i + 1}</b> · ${fmtDur(st.minutes)}<br/>${fmtDT(st.startAt)} → ${timeOnly(st.endAt)}`)));
    }

    // 5 — the truck itself, always on top
    const last = locations[locations.length - 1];
    if (last && trip.status === 'ACTIVE') {
      const truck = L.marker([last.latitude, last.longitude], {
        icon: L.divIcon({ className: 'fx-truck', html: '<div class="fx-truck-b">🚛</div>', iconSize: [40, 40], iconAnchor: [20, 20] }),
        zIndexOffset: 1000,
      }).bindPopup(`<b>${escapeHtml(trip.vehicle?.vehicleNumber || 'Truck')}</b><br/>${Math.round(last.speed || 0)} km/h · ${fmtDT(last.timestamp)}`);
      truck.on('click', () => zoomTo(map, truck.getLatLng()));
      add(truck);
    }

    const all: LatLngTuple[] = [...planned, ...trail];
    // Recentre always shows the whole journey, whatever the view is doing.
    map.fleetSetHome(all);

    const running = trip.status === 'ACTIVE' && last;
    if (!fitted.current) {
      // A finished trip is a thing to read, so it opens as the whole route. A
      // running one is a question — where is the truck now — and fitting a
      // 180 km route answers it with a dot. So it opens on the truck.
      if (running) map.setView([last.latitude, last.longitude], VEHICLE_ZOOM, { animate: false });
      else map.fitBounds(all, { padding: [18, 18], animate: false });
      fitted.current = true;
    } else if (running && follow.current) {
      // Pan only when the truck has actually left the frame, so a truck sitting
      // in view does not nudge the map every fifteen seconds. The inset keeps it
      // off the very edge, where it is as good as gone.
      const at = window.L.latLng(last.latitude, last.longitude);
      if (!map.getBounds().pad(-0.12).contains(at)) map.panTo(at, { animate: true, duration: 0.8 });
    }
  }, [data, showPlanned, showHalts, showDeviations]);

  // ── Replay loop ───────────────────────────────────────────────────────────
  // Steps a marker along the recorded points so the whole trip plays in ~35 s,
  // however many points it has.
  useEffect(() => {
    const map = mapRef.current, L = window.L;
    const pts = data?.locations || [];
    // Pausing leaves the truck and its readout where they are — the point of
    // pausing is to read the time and speed at that moment. `clear` is the full
    // teardown, used when the trip itself changes.
    const stop = (clear = false) => {
      if (replayTimer.current) { clearInterval(replayTimer.current); replayTimer.current = null; }
      if (clear) {
        if (replayMk.current && map) { map.removeLayer(replayMk.current); replayMk.current = null; }
        setReplayAt(null);
      }
    };
    if (!playing || !map || !L || pts.length < 2) { stop(); return; }

    // Resume from where the pause left off rather than jumping back to the start.
    const resumeFrom = replayAt ? pts.findIndex((p) => p.id === replayAt.id) : -1;
    let i = resumeFrom >= 0 && resumeFrom < pts.length - 1 ? resumeFrom : 0;
    const step = Math.max(1, Math.round(pts.length / 280));
    if (!replayMk.current) {
      replayMk.current = L.marker([pts[i].latitude, pts[i].longitude], {
        icon: L.divIcon({ className: 'fx-replay', html: '<div class="fx-replay-b">🚚</div>', iconSize: [28, 28], iconAnchor: [14, 14] }),
        zIndexOffset: 1200,
      }).addTo(map);
    }
    setReplayAt(pts[i]);

    replayTimer.current = setInterval(() => {
      i += step;
      if (i >= pts.length) { setPlaying(false); return; }
      const p = pts[i];
      replayMk.current?.setLatLng([p.latitude, p.longitude]);
      setReplayAt(p);
    }, 120);

    return () => stop();
    // `replayAt` is read to resume from the pause point, but must not re-run this.
  }, [playing, data]);

  // A different trip (or a fresh trail) wipes the replay entirely.
  useEffect(() => {
    const map = mapRef.current;
    if (replayMk.current && map) { map.removeLayer(replayMk.current); replayMk.current = null; }
    setReplayAt(null);
    setPlaying(false);
  }, [id]);

  if (!data) {
    return (
      <div style={{ ...P.page, textAlign: 'center' }}><div style={{ height: 40 }} />
        {loadErr ? (
          <>
            <div style={{ fontSize: 32, marginBottom: 10 }}>⚠️</div>
            <h2 style={P.h1}>{loadErr}</h2>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
              <button type="button" style={P.btnGhost} onClick={() => navigate('/fleet/trips')}>← Trips</button>
              <button type="button" style={P.btnPrimary} onClick={() => { setLoadErr(''); load(); }}>⟳ Try Again</button>
            </div>
          </>
        ) : <span style={P.sub}>Loading trip…</span>}
      </div>
    );
  }

  const { trip, dealers, metrics, locations, activities, ifmsLogs, ifms, deviations } = data;
  // The same rule the trip list uses, from the same module, so one screen
  // cannot call a trip something the other does not.
  const badge = tripBadge({ status: trip.status, ifms: ifms.state }, false);
  const st = { label: badge.label, bg: trip.status === 'COMPLETED' && ifms.state !== 'sent' ? '#8E8E93' : STATUS[trip.status].bg };
  const progress = progressPct(trip, locations, replayAt);
  const open = trip.status === 'PENDING' || trip.status === 'ACTIVE';
  const stopsCount = data.stops.length;
  const missing = ifms.missing.TRIP_START;

  const act = async (name: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(name); setNotice(null);
    try { await fn(); await load(); setNotice({ tone: 'ok', text: ok }); }
    catch (e) { setNotice({ tone: 'err', text: apiError(e, 'Action failed') }); }
    finally { setBusy(''); }
  };
  const resend = (apiName: IfmsApi) => act(`ifms-${apiName}`, () => sendIfms(id, apiName), 'Sent to IFMS — see API Logs for the result');

  // ── Detail rows ──
  const need = (v: unknown, required = false): React.ReactNode =>
    v != null && String(v).trim() !== '' ? String(v) : required ? <span style={S.missing}>Missing</span> : '—';

  return (
    <div style={{ ...P.page, maxWidth: 1400 }}>
      <style>{PAGE_CSS}</style>

      {/* Header */}
      <div style={P.head}>
        <div>
          <h1 style={P.h1}>Trip Details : {trip.tripNumber}</h1>
          <p style={P.sub}>{trip.fromLocation} → {trip.toLocation}</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', position: 'relative' }}>
          <button type="button" style={P.btnGhost} onClick={() => navigate('/fleet/trips')}>← Trips</button>
          {canEdit && trip.status !== 'CANCELLED' && (
            <button type="button" style={P.btnPrimary} onClick={() => setEditOpen(true)}><Icon name="edit" size={14} /> Edit</button>
          )}
          {canAct && open && (
            <button type="button" style={P.btnPrimary} onClick={() => setStatusOpen(true)}><Icon name="edit" size={14} /> Status</button>
          )}
        </div>
      </div>

      {notice && (
        <div role="status" style={{ ...S.notice, ...(notice.tone === 'err' ? S.noticeErr : S.noticeOk) }}>
          <span style={{ flex: 1 }}>{notice.text}</span>
          <button type="button" style={S.noticeX} onClick={() => setNotice(null)} aria-label="Dismiss">✕</button>
        </div>
      )}
      {trip.status === 'PENDING' && (
        <div style={S.warn}>
          <b>Waiting for departure.</b> The trip starts by itself when the truck is 1.5 km clear of the plant and moving —
          that moment is recorded as the start time and is what IFMS receives. You can also start it by hand from <b>Status</b>.
        </div>
      )}
      {open && missing.length > 0 && (
        <div style={S.warn}>
          <b>IFMS will not receive this trip yet.</b> Missing: {missing.join(', ')}.
          {canEdit && <button type="button" style={S.warnBtn} onClick={() => setEditOpen(true)}>Fill now</button>}
        </div>
      )}

      {/* Details + Route */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap: 16, marginBottom: 16, alignItems: 'start' }}>
        <div style={S.panel}>
          <div style={S.panelHead}>
            <div><div style={S.panelTitle}>Trip Details</div><div style={S.panelSub}>Trip information and booking details</div></div>
            <span style={{ ...S.statusPill, background: st.bg }}>{st.label}</span>
          </div>

          <Section icon="truck" title="Trip Information">
            <Row cells={[['Trip Name', need(trip.tripName)], ['Booking Date', dateOnly(trip.createdAt)]]} />
            {/* Registration number and the GPS company, nothing else: when a trail
                looks wrong the first question is whose feed drew it, and three of
                them run at once. The vehicle type sat here before and said nothing
                useful — every truck in the fleet is a TRUCK. The name comes from
                gpsProvider.ts so it reads the way the company writes it, and the
                same way the Create Trip picker shows it. */}
            <Row cells={[['Vehicle Number', trip.vehicle
              ? `${trip.vehicle.vehicleNumber}${trip.vehicle.gpsProvider ? ` - ${providerName(trip.vehicle.gpsProvider)}` : ''}`
              : '—'], ['GPS Device', need(trip.vehicle?.gpsDeviceId)]]} />
          </Section>
          <Section icon="users" title="Driver Information">
            <Row cells={[['Driver Name', need(trip.driverName || trip.vehicle?.driverName)],
              ['Driver Phone', (trip.driverMobile || trip.vehicle?.driverMobile)
                ? <a href={`tel:${trip.driverMobile || trip.vehicle?.driverMobile}`} style={S.link}>📞 {trip.driverMobile || trip.vehicle?.driverMobile}</a> : '—']]} />
          </Section>
          <Section icon="pin" title="Route">
            <Row cells={[['Origin', <span style={S.place}><i style={{ ...S.dot, background: '#34C759' }} />{trip.fromLocation}</span>]]} />
            <Row cells={[['Destination', <span style={S.place}><i style={{ ...S.dot, background: '#FF3B30' }} />{trip.toLocation}</span>]]} />
          </Section>
          <Section icon="clock" title="Trip Schedule">
            <Row cells={[['Estimated Start', <DT v={trip.estStart} />], ['Estimated End', <DT v={trip.estEnd} />]]} />
            <Row cells={[['Estimated KM', kmText(metrics.estimatedKm)], ['Estimated Time', fmtDur(metrics.estimatedMin)]]} />
            <Row cells={[['Actual Start', <DT v={trip.startedAt} />], ['Actual End', <DT v={trip.completedAt || trip.cancelledAt} />]]} />
            <Row cells={[[metrics.departed ? 'Waited at plant' : 'Waiting at plant',
              <span style={{ color: metrics.waitingMin >= 240 ? 'var(--fx-orange)' : 'var(--fx-text)' }}>
                {fmtDur(metrics.waitingMin)}
                <span style={{ color: 'var(--fx-text-3)', fontWeight: 400 }}>
                  {metrics.departed ? ' · between booking and departure' : ' · truck has not left yet'}
                </span>
              </span>]]} />
          </Section>
          <Section icon="database" title="Shipment Information">
            <Row cells={[['Dispatch Code (DD)', need(trip.ddCode, true)], ['Dealer Code', need(trip.dealerCode, true)]]} />
            <Row cells={[['Company Code', need(trip.companyCode, true)], ['Plant / Port Code', need(trip.plantPortCode, true)]]} />
            <Row cells={[['Product Code', need(trip.productCode, true)], ['Waybill Number', need(trip.waybillNumber)]]} />
            <Row cells={[['Invoice Number', need(trip.invoiceNumber, true)], ['Quantity Dispatched', trip.quantityMt != null ? `${trip.quantityMt} MT` : <span style={S.missing}>Missing</span>]]} />
            {trip.ackQuantityMt != null && <Row cells={[['Quantity Acknowledged', `${trip.ackQuantityMt} MT`]]} />}
            {trip.sourceType && trip.sourceType !== 'PLANT' && (
              <Row cells={[['Source', `${humanize(trip.sourceType)} · ${trip.portCode || trip.rakepointId || trip.warehouseId || '—'}`]]} />
            )}
            <Row cells={[['Dispatch Code', trip.dispatchCode ? `${trip.dispatchCode}${trip.dispatchType ? ` - ${trip.dispatchType}` : ''}` : <span style={S.missing}>Missing</span>],
              ['Dest. State / District', (trip.destStateCode || trip.destDistrictCode)
                ? `${trip.destStateCode || '—'} / ${trip.destDistrictCode || '—'}` : <span style={S.missing}>Missing</span>]]} />
          </Section>
          <Section icon="list" title="Additional Information" last>
            <Row cells={[['Created By', need(trip.createdByName)], ['Created At', fmtDT(trip.createdAt)]]} />
            {trip.cancelReason && <Row cells={[['Cancel Reason', trip.cancelReason]]} />}
          </Section>
        </div>

        {/* Sized by its own content, and pinned while the long details list scrolls,
            so the route, its legend and the replay readout stay together on screen. */}
        <div style={{
          ...S.panel, display: 'flex', flexDirection: 'column',
          ...(isMobile ? {} : { position: 'sticky', top: 14 }),
        }}>
          <div style={S.panelHead}>
            <div><div style={S.panelTitle}>🗺 Trip Route</div><div style={S.panelSub}>Origin to destination</div></div>
            <span style={S.kmBadge}>{kmText(metrics.estimatedKm)}</span>
          </div>
          {/* The panel's own head and foot take ~120px; the rest of the screen is the
              map. Capped so the legend, the progress bar and the replay readout are
              always visible together — reading the trail should not need a scroll. */}
          <div style={{
            position: 'relative', isolation: 'isolate', zIndex: 0,
            height: isMobile ? 360 : 'clamp(380px, calc(100vh - 250px), 600px)',
          }}>
            <div ref={mapEl} style={{ position: 'absolute', inset: 0 }} />
            {locations.length > 1 && (
              <button type="button" style={{ ...S.showTruck, right: trip.status === 'ACTIVE' && locations.length > 0 ? 136 : 12 }}
                onClick={() => setPlaying((v) => !v)}>
                {playing ? '⏸ Pause' : '▶ Replay trip'}
              </button>
            )}
            {replayAt && (
              <div style={S.replayInfo}>
                <span style={S.replayClock}>🚚 {fmtDT(replayAt.timestamp)}</span>
                <span style={S.replaySep} />
                <span style={S.replaySpeed}>{Math.round(replayAt.speed || 0)} km/h</span>
                {!playing && <span style={S.replayPaused}>paused</span>}
              </div>
            )}
            {trip.status === 'ACTIVE' && locations.length > 0 && (
              <button type="button" style={S.showTruck}
                onClick={() => {
                  follow.current = true;
                  const c = locations[locations.length - 1];
                  if (mapRef.current) zoomTo(mapRef.current, [c.latitude, c.longitude]);
                }}>
                🚛 Show Truck
              </button>
            )}
          </div>
          <div style={S.mapFoot}>
            <Toggle on={showPlanned} onClick={() => setShowPlanned((v) => !v)} color={PLANNED} dashed label="Planned route" />
            <Toggle on onClick={() => {}} color={TRAVELLED} label="Travelled" fixed />
            <Toggle on={showDeviations} onClick={() => setShowDeviations((v) => !v)} color={OFF_ROUTE}
              label={`Off route${deviations.length ? ` (${deviations.length})` : ''}`} />
            <Toggle on={showHalts} onClick={() => setShowHalts((v) => !v)} color="#FF9500" dot
              label={`Halts${stopsCount ? ` (${stopsCount})` : ''}`} />
            <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, minWidth: 160 }}>
              {trip.status === 'ACTIVE' && <span className="live-dot" />}
              <span style={S.progTrack}>
                <span style={{ ...S.progFill, width: `${progress}%`, transition: replayAt ? 'width .12s linear' : 'width .6s' }} />
              </span>
              <b style={{ fontSize: 12, color: replayAt ? 'var(--fx-accent)' : 'var(--fx-text)', fontVariantNumeric: 'tabular-nums', minWidth: 34, textAlign: 'right' }}>{progress}%</b>
            </span>
          </div>
        </div>
      </div>

      {/* What happened, in order. Above the analysis on purpose: the numbers
          below are easier to read once the sequence they came from is known. */}
      <TripTimeline details={data} />

      {/* Dealer-wise consignment (IFMS dispatchDetails) */}
      <DealerPanel trip={trip} dealers={dealers} canEdit={canEdit}
        onChanged={async (text) => { await load(); setNotice({ tone: 'ok', text }); }} />

      {/* Distance / time analysis */}
      <Analysis m={metrics} isMobile={isMobile} />

      {/* Logs */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1fr)', gap: 16, marginBottom: 16 }}>
        <div>
          <div style={S.blockTitle}>Activity Logs <span style={S.blockSub}>Recent trip activities</span></div>
          <ReportTable<TripActivityRow> title={`Activity Logs ${trip.tripNumber}`} columns={ACTIVITY_COLS} rows={activities}
            emptyText="No activity yet" initialSort={{ key: 'createdAt', dir: -1 }} pageSize={10} minWidth={isMobile ? 560 : 0} />
        </div>
        <div>
          <div style={S.blockTitle}>Vehicle Logs <span style={S.blockSub}>GPS points recorded on this trip</span></div>
          <ReportTable<TripLocation & { sr: number }> title={`Vehicle Logs ${trip.tripNumber}`} columns={VEHICLE_LOG_COLS}
            rows={locations.map((l, i) => ({ ...l, sr: i + 1 }))} rowKey={(r) => String(r.sr)} pageSize={10} minWidth={isMobile ? 520 : 0}
            emptyText={trip.status === 'PENDING' ? 'Tracking starts when the trip is started' : 'No GPS points yet'} />
        </div>
      </div>

      <IfmsPanel logs={ifmsLogs} configured={ifms.configured} trip={trip} busy={busy} canAct={canAct} onSend={resend} />

      {editOpen && (
        <UpdateTripModal trip={trip} onClose={() => setEditOpen(false)}
          onSaved={async () => { setEditOpen(false); await load(); setNotice({ tone: 'ok', text: 'Trip updated' }); }}
          onFullEdit={() => navigate(`/fleet/trips/${id}/edit`)} canFullEdit={open} />
      )}
      {statusOpen && (
        <StatusModal trip={trip} onClose={() => setStatusOpen(false)}
          onDone={async (text) => { setStatusOpen(false); await load(); setNotice({ tone: 'ok', text }); }} />
      )}
    </div>
  );
}

/** Legend chip that doubles as a layer switch. */
function Toggle({ on, onClick, color, label, dashed, dot, fixed }: {
  on: boolean; onClick: () => void; color: string; label: string; dashed?: boolean; dot?: boolean; fixed?: boolean;
}) {
  return (
    <button type="button" onClick={fixed ? undefined : onClick} aria-pressed={on} disabled={fixed}
      style={{ ...S.legendBtn, opacity: on ? 1 : 0.45, cursor: fixed ? 'default' : 'pointer' }}
      title={fixed ? label : on ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}>
      {dot
        ? <i style={{ ...S.legDot, background: color }} />
        : <i style={{ ...S.legLine, background: dashed ? 'transparent' : color, backgroundImage: dashed ? `repeating-linear-gradient(90deg, ${color} 0 3px, transparent 3px 6px)` : undefined }} />}
      {label}
    </button>
  );
}

// ── Detail table pieces ───────────────────────────────────────────────────────
function Section({ icon, title, children, last }: { icon: IconName; title: string; children: React.ReactNode; last?: boolean }) {
  return (
    <div style={{ borderBottom: last ? 'none' : '1px solid var(--fx-border)' }}>
      <div style={S.secHead}><Icon name={icon} size={14} stroke={2.2} style={{ color: 'var(--fx-accent)' }} />{title}</div>
      {children}
    </div>
  );
}
function Row({ cells }: { cells: [string, React.ReactNode][] }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: cells.length === 1 ? '150px 1fr' : '150px 1fr 150px 1fr', borderTop: '1px solid var(--fx-border)' }} className="fx-drow">
      {cells.map(([l, v]) => (
        <React.Fragment key={l}>
          <div style={S.dLbl}>{l}</div>
          <div style={S.dVal}>{v}</div>
        </React.Fragment>
      ))}
    </div>
  );
}
function DT({ v }: { v: string | null | undefined }) {
  if (!v) return <>—</>;
  return <span>{dateOnly(v)}<br /><span style={{ color: 'var(--fx-text-2)' }}>{timeOnly(v)}</span></span>;
}

// ── Analysis (donut + metric tiles) ───────────────────────────────────────────
function Analysis({ m, isMobile }: { m: TripDetails['metrics']; isMobile: boolean }) {
  const pie = [
    { name: 'Estimated KM', value: m.estimatedKm || 0, color: '#4DA3FF' },
    { name: 'Actual KM', value: m.actualKm || 0, color: '#5856D6' },
    { name: 'Deviation KM', value: m.deviationKm || 0, color: '#34C759' },
  ];
  const hasPie = pie.some((p) => p.value > 0);
  const diffTone = m.differentialMin == null ? 'var(--fx-text)' : m.differentialMin > 30 ? 'var(--fx-red)' : 'var(--fx-green)';
  const tiles: { icon: IconName; tint: string; label: string; value: React.ReactNode; hint?: string }[] = [
    { icon: 'truck', tint: '#5856D6', label: 'Vehicle Running Time', value: fmtDur(m.runningMin), hint: `Halted ${fmtDur(m.haltMin)}` },
    { icon: 'map', tint: '#34C759', label: 'Vehicle Estimated / Actual KM', value: `${m.estimatedKm ?? '—'} / ${m.actualKm} KM`,
      // Without this, a distance that is a third straight-line guesswork looks
      // exactly like one measured the whole way. The hint only appears when
      // there was a gap, so an unbroken trip is not cluttered with a caveat it
      // does not need.
      hint: m.gapCount
        ? `${m.observedKm ?? '—'} KM seen on the trail, ${m.bridgedKm ?? '—'} KM across ${m.gapCount} GPS gap(s)`
          + (m.completeness != null ? ` — ${Math.round(m.completeness * 100)}% observed` : '')
        : 'Unbroken GPS trail' },
    // Against the planned road, not the straight line, so it answers the
    // question an operator actually asks: how much further has it to drive.
    ...(m.estimatedKm != null ? [{
      icon: 'pin' as IconName, tint: '#0EA5E9', label: 'Distance Remaining',
      value: `${Math.max(0, Math.round((m.estimatedKm - m.actualKm) * 10) / 10)} KM`,
      hint: m.actualKm > m.estimatedKm ? 'Already past the planned distance' : 'Against the planned road',
    }] : []),
    { icon: 'clock', tint: '#FF9500', label: 'Estimate Time', value: fmtDur(m.estimatedMin), hint: 'Road driving time' },
    { icon: 'clock', tint: '#8E8E93', label: 'Waiting At Plant', value: fmtDur(m.waitingMin),
      hint: m.departed ? 'Booking → actual departure' : 'Truck has not left yet' },
    { icon: 'pin', tint: '#FF3B30', label: 'Total Deviation', value: `${m.deviationKm} KM`,
      hint: m.maxDeviationKm ? `Farthest ${m.maxDeviationKm} km off route${m.offRouteNow ? ' · off route now' : ''}` : `Off route = more than ${m.roadDeviationKm} km from the road` },
    { icon: 'clock', tint: '#AF52DE', label: 'Differential Time', value: <span style={{ color: diffTone }}>{signedDur(m.differentialMin)}</span>,
      hint: m.elapsedMin != null ? `Trip time ${fmtDur(m.elapsedMin)} vs estimate` : 'Starts counting when the trip starts' },
    { icon: 'chart', tint: '#30B0C7', label: 'No. of Stops and Diversion', value: `${m.stops} / ${m.diversions}`,
      hint: `Stops ≥ ${m.stopMin} min${m.unauthorizedHalts ? ` · ${m.unauthorizedHalts} unauthorized` : ''}` },
  ];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1.35fr)', gap: 16, marginBottom: 16 }}>
      <div style={{ ...S.panel, padding: 18 }}>
        <div style={{ ...S.panelTitle, textAlign: 'center' }}>Trip Distance Analysis (KM)</div>
        <div style={{ height: 230 }}>
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={hasPie ? pie : [{ name: 'No data', value: 1, color: '#8E8E9333' }]} dataKey="value" cx="50%" cy="50%"
                innerRadius={55} outerRadius={90} paddingAngle={hasPie ? 2 : 0} stroke="none">
                {(hasPie ? pie : [{ color: '#8E8E9333' }]).map((p, i) => <Cell key={i} fill={p.color} />)}
              </Pie>
              {hasPie && <Tooltip formatter={(v: number) => `${v} KM`} />}
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap' }}>
          {pie.map((p) => (
            <span key={p.name} style={S.legend}><i style={{ ...S.legDot, background: p.color }} />{p.name}: <b style={{ color: 'var(--fx-text)' }}>{p.value}</b></span>
          ))}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
        {tiles.map((t) => (
          <div key={t.label} style={S.tile}>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fx-text)' }}>{t.label}</div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 12 }}>
              <span style={{ ...S.tileIcon, background: `${t.tint}22`, color: t.tint }}><Icon name={t.icon} size={16} stroke={2.1} /></span>
              <span style={S.tileVal}>{t.value}</span>
            </div>
            {t.hint && <div style={S.tileHint}>{t.hint}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Log tables ────────────────────────────────────────────────────────────────
const ACTION_TONE: Record<string, 'green' | 'blue' | 'orange' | 'red' | 'gray' | 'purple'> = {
  CREATED: 'blue', UPDATED: 'gray', STARTED: 'green', COMPLETED: 'green', CANCELLED: 'red', IFMS: 'purple',
};
const ACTIVITY_COLS: RCol<TripActivityRow>[] = [
  { key: 'action', label: 'Activity', value: (r) => humanize(r.action), render: (r) => <Tag text={humanize(r.action)} tone={ACTION_TONE[r.action] || 'gray'} /> },
  { key: 'message', label: 'Description' },
  { key: 'actor', label: 'By', value: (r) => r.actor || '—' },
  { key: 'createdAt', label: 'Time', value: (r) => fmtDT(r.createdAt), sortValue: (r) => r.createdAt, render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDT(r.createdAt)}</span> },
];
const VEHICLE_LOG_COLS: RCol<TripLocation & { sr: number }>[] = [
  { key: 'timestamp', label: 'Date & Time', value: (r) => fmtDT(r.timestamp), sortValue: (r) => r.timestamp, render: (r) => <span style={{ whiteSpace: 'nowrap' }}>{fmtDT(r.timestamp)}</span> },
  { key: 'latlng', label: 'Latitude & Longitude', value: (r) => `${r.latitude.toFixed(6)}, ${r.longitude.toFixed(6)}`,
    render: (r) => <a href={`https://www.google.com/maps?q=${r.latitude},${r.longitude}`} target="_blank" rel="noreferrer" style={{ color: 'var(--fx-accent)', whiteSpace: 'nowrap' }}>{r.latitude.toFixed(6)}, {r.longitude.toFixed(6)}</a> },
  { key: 'speed', label: 'Speed', align: 'right', value: (r) => `${Math.round(r.speed || 0)} km/h`, sortValue: (r) => r.speed ?? 0 },
  { key: 'ignition', label: 'Ignition', value: (r) => (r.ignition == null ? '—' : r.ignition ? 'ON' : 'OFF'),
    render: (r) => (r.ignition == null ? <span style={{ color: 'var(--fx-text-3)' }}>—</span> : <Tag text={r.ignition ? 'ON' : 'OFF'} tone={r.ignition ? 'green' : 'red'} />) },
];

// ── Dealer-wise consignment lines ─────────────────────────────────────────────
// IFMS allows one truck to carry fertilizer for several dealers: the trip is one
// vehicle trip, and each dealer is a dispatchDetails[] / receiptDetails[] entry.
const DEALER_TYPES = ['RETAILER', 'WHOLESALER', 'RAKEPOINT'];
const typeTone: Record<string, 'blue' | 'purple' | 'gray'> = { RETAILER: 'blue', WHOLESALER: 'purple', RAKEPOINT: 'gray' };

function DealerPanel({ trip, dealers, canEdit, onChanged }: {
  trip: Trip; dealers: TripDealerRow[]; canEdit: boolean; onChanged: (text: string) => Promise<void> | void;
}) {
  const [edit, setEdit] = useState<{ row: TripDealerRow | null } | null>(null);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const total = dealers.reduce((n, d) => n + d.quantityMt, 0);
  const ackTotal = dealers.reduce((n, d) => n + (d.quantityAckMt ?? 0), 0);
  const left = trip.quantityMt != null ? Math.round((trip.quantityMt - total) * 100) / 100 : null;

  const remove = async (row: TripDealerRow) => {
    setBusy(row.id); setErr('');
    try { await deleteTripDealer(trip.id, row.id); await onChanged('Dealer line removed'); }
    catch (e) { setErr(apiError(e, 'Could not remove the dealer line')); }
    finally { setBusy(''); }
  };

  return (
    <div style={{ ...S.panel, marginBottom: 16 }}>
      <div style={S.panelHead}>
        <div>
          <div style={S.panelTitle}>Consignment — Dealer wise</div>
          <div style={S.panelSub}>
            One truck may carry fertilizer for several dealers. Each line is sent to IFMS as its own dispatch / receipt entry.
          </div>
        </div>
        {canEdit && trip.status !== 'CANCELLED' && (
          <button type="button" style={P.btnPrimary} onClick={() => { setErr(''); setEdit({ row: null }); }}>＋ Add Dealer</button>
        )}
      </div>
      {err && <div style={{ ...S.ifmsNote, background: 'var(--fx-red-soft)', color: 'var(--fx-red)' }}>{err}</div>}

      {dealers.length === 0 ? (
        <div style={{ padding: '18px 18px 20px', fontSize: 12.5, color: 'var(--fx-text-2)', lineHeight: 1.6 }}>
          No dealer lines yet — IFMS will receive this trip as a single dealer, using the Dealer Code, destination codes,
          product and quantity from the trip details above.
          {canEdit && trip.status !== 'CANCELLED' && <> Add a line here when one truck serves more than one dealer.</>}
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ ...P.table, minWidth: 900 }}>
            <thead><tr>{['#', 'Dealer Code', 'Name', 'Type', 'State / District', 'Product', 'Qty (MT)', 'Ack. (MT)', 'Receipt Id', ''].map((h) => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
            <tbody>
              {dealers.map((d, i) => (
                <tr key={d.id}>
                  <td style={{ ...P.td, color: 'var(--fx-text-3)' }}>{i + 1}</td>
                  <td style={{ ...P.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{d.dispatchToCode}</td>
                  <td style={P.td}>{d.dealerName || '—'}</td>
                  <td style={P.td}><Tag text={humanize(d.dispatchToType)} tone={typeTone[d.dispatchToType] || 'gray'} /></td>
                  <td style={{ ...P.td, whiteSpace: 'nowrap' }}>{d.destStateCode} / {d.destDistrictCode}</td>
                  <td style={P.td}>{d.productCode}</td>
                  <td style={{ ...P.td, textAlign: 'right' }}>{d.quantityMt}</td>
                  <td style={{ ...P.td, textAlign: 'right' }}>{d.quantityAckMt ?? '—'}</td>
                  <td style={P.td}>{d.dispatchReceiptId || '—'}</td>
                  <td style={{ ...P.td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {canEdit && trip.status !== 'CANCELLED' && (
                      <>
                        <button type="button" style={S.rowBtn} title="Edit" onClick={() => { setErr(''); setEdit({ row: d }); }}><Icon name="edit" size={14} stroke={2} /></button>
                        <button type="button" style={{ ...S.rowBtn, color: 'var(--fx-red)' }} title="Remove" disabled={busy === d.id} onClick={() => remove(d)}><Icon name="trash" size={14} stroke={2} /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              <tr>
                <td style={{ ...P.td, fontWeight: 600 }} colSpan={6}>Total</td>
                <td style={{ ...P.td, textAlign: 'right', fontWeight: 700 }}>{Math.round(total * 100) / 100}</td>
                <td style={{ ...P.td, textAlign: 'right', fontWeight: 700 }}>{ackTotal ? Math.round(ackTotal * 100) / 100 : '—'}</td>
                <td style={P.td} colSpan={2}>
                  {left != null && (left > 0
                    ? <span style={{ color: 'var(--fx-orange)', fontSize: 12 }}>{left} MT of the dispatched {trip.quantityMt} MT not assigned</span>
                    : <span style={{ color: 'var(--fx-green)', fontSize: 12 }}>Matches the dispatched quantity</span>)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <DealerModal trip={trip} row={edit.row} onClose={() => setEdit(null)}
          onSaved={async () => { setEdit(null); await onChanged(edit.row ? 'Dealer line updated' : 'Dealer line added'); }} />
      )}
    </div>
  );
}

function DealerModal({ trip, row, onClose, onSaved }: { trip: Trip; row: TripDealerRow | null; onClose: () => void; onSaved: () => void }) {
  // Adding the first line starts from the trip's own dealer details.
  const [form, setForm] = useState<Record<string, string>>(() => ({
    dispatchToCode: row?.dispatchToCode ?? trip.dealerCode ?? '',
    dealerName: row?.dealerName ?? '',
    dispatchToType: row?.dispatchToType ?? (trip.dispatchType?.toUpperCase() === 'DEALER' ? 'WHOLESALER' : 'RETAILER'),
    destStateCode: row?.destStateCode ?? trip.destStateCode ?? '',
    destDistrictCode: row?.destDistrictCode ?? trip.destDistrictCode ?? '',
    productCode: row?.productCode ?? trip.productCode ?? '',
    proposedDistanceKm: String(row?.proposedDistanceKm ?? trip.estimatedKm ?? ''),
    quantityMt: String(row?.quantityMt ?? ''),
    quantityAckMt: String(row?.quantityAckMt ?? ''),
    dispatchReceiptId: row?.dispatchReceiptId ?? '',
  }));
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setErr('');
    setSaving(true);
    try {
      const body = { ...form, quantityMt: form.quantityMt, quantityAckMt: form.quantityAckMt || null, proposedDistanceKm: form.proposedDistanceKm || null };
      if (row) await updateTripDealer(trip.id, row.id, body);
      else await addTripDealer(trip.id, body);
      onSaved();
    } catch (e2) { setErr(apiError(e2, 'Could not save the dealer line')); }
    finally { setSaving(false); }
  };

  const field = (key: string, label: string, placeholder = '', numeric = false) => (
    <div>
      <label style={S.fLabel}>{label}</label>
      <input style={S.fInput} value={form[key]} placeholder={placeholder} inputMode={numeric ? 'decimal' : undefined}
        onChange={(e) => set(key, e.target.value)} />
    </div>
  );

  return (
    <Modal title={row ? 'Edit Dealer Line' : 'Add Dealer Line'} subtitle={`${trip.tripNumber} · sent to IFMS as a dispatch / receipt entry`}
      onClose={onClose} width={680} footer={false}>
      <form onSubmit={save}>
        <div style={S.formGrid}>
          {field('dispatchToCode', 'Dealer Code *', 'e.g. 56789')}
          {field('dealerName', 'Dealer Name', 'e.g. Sri Sai Traders')}
          <div>
            <label style={S.fLabel}>Dispatch Type *</label>
            <select style={S.fInput} value={form.dispatchToType} onChange={(e) => set('dispatchToType', e.target.value)}>
              {DEALER_TYPES.map((t) => <option key={t} value={t}>{humanize(t)}</option>)}
            </select>
          </div>
          {field('productCode', 'Product Code *', 'e.g. 1089')}
          {field('destStateCode', 'Destination State Code *', 'e.g. 36')}
          {field('destDistrictCode', 'Destination District Code *', 'e.g. 2834')}
          {field('quantityMt', 'Quantity Dispatched (MT) *', 'e.g. 19.98', true)}
          {field('proposedDistanceKm', 'Proposed Distance (KM)', 'e.g. 44.63', true)}
          {field('quantityAckMt', 'Quantity Acknowledged (MT)', 'filled at delivery', true)}
          {field('dispatchReceiptId', 'Receipt Id', 'acknowledgement id')}
        </div>
        {err && <div style={S.formErr}>{err}</div>}
        <div style={S.formFoot}>
          <span style={{ flex: 1 }} />
          <button type="button" style={S.btnCancel} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...S.btnUpdate, opacity: saving ? 0.7 : 1 }} disabled={saving}>{saving ? 'Saving…' : row ? 'Update' : 'Add'}</button>
        </div>
      </form>
    </Modal>
  );
}

const IFMS_ORDER: IfmsApi[] = ['TRIP_START', 'TRIP_END', 'TRIP_CLOSURE'];
const IFMS_LABEL: Record<IfmsApi, string> = { TRIP_START: 'Trip Start API', TRIP_END: 'Trip End API', TRIP_CLOSURE: 'Trip Closure API' };
const IFMS_TONE = { SUCCESS: 'green', FAILED: 'red', NOT_SENT: 'orange' } as const;
const IFMS_TEXT = { SUCCESS: 'success', FAILED: 'failed', NOT_SENT: 'not sent' } as const;

function IfmsPanel({ logs, configured, trip, busy, canAct, onSend }: {
  logs: IfmsLogRow[]; configured: boolean; trip: Trip; busy: string; canAct: boolean; onSend: (api: IfmsApi) => void;
}) {
  const byApi = Object.fromEntries(logs.map((l) => [l.api, l])) as Partial<Record<IfmsApi, IfmsLogRow>>;
  const sendable = (api: IfmsApi) => {
    const l = byApi[api];
    if (l?.status === 'SUCCESS') return false;
    if (api === 'TRIP_START') return trip.status === 'ACTIVE' || trip.status === 'COMPLETED';
    // IFMS order: End needs a successful Start, Closure a successful End.
    return trip.status === 'COMPLETED' && byApi[api === 'TRIP_END' ? 'TRIP_START' : 'TRIP_END']?.status === 'SUCCESS';
  };
  const waiting: Record<IfmsApi, string> = {
    TRIP_START: trip.status === 'PENDING' ? 'Sent automatically when the trip starts' : 'Not triggered yet',
    TRIP_END: 'Sent automatically when the trip is completed',
    TRIP_CLOSURE: 'Sent automatically after Trip End succeeds',
  };
  return (
    <div style={{ ...S.panel, marginBottom: 16 }}>
      <div style={S.panelHead}>
        <div><div style={S.panelTitle}>API Logs</div><div style={S.panelSub}>IFMS (VLTS) calls for this trip · Trip Start → Trip End → Trip Closure</div></div>
        <Tag text={configured ? 'IFMS connected' : 'IFMS not configured'} tone={configured ? 'green' : 'orange'} />
      </div>
      {!configured && (
        <div style={S.ifmsNote}>
          IFMS credentials are not set on this server, so calls are prepared and checked but not sent. Once the Client Id / Secret are added, use <b>Send</b> below.
        </div>
      )}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...P.table, minWidth: 820 }}>
          <thead><tr>{['API Name', 'Status', 'Remarks', 'Attempts', 'Last Updated', ''].map((h) => <th key={h} style={P.th}>{h}</th>)}</tr></thead>
          <tbody>
            {IFMS_ORDER.map((api) => {
              const l = byApi[api];
              return (
                <tr key={api}>
                  <td style={{ ...P.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{IFMS_LABEL[api]}</td>
                  <td style={P.td}>{l ? <Tag text={IFMS_TEXT[l.status]} tone={IFMS_TONE[l.status]} /> : <Tag text="waiting" tone="gray" />}</td>
                  <td style={{ ...P.td, maxWidth: 520 }}>
                    {l ? <code style={S.code}>{l.remarks}</code> : <span style={{ color: 'var(--fx-text-3)' }}>{waiting[api]}</span>}
                  </td>
                  <td style={{ ...P.td, textAlign: 'right' }}>{l?.attempts ?? '—'}</td>
                  <td style={{ ...P.td, whiteSpace: 'nowrap' }}>{l ? fmtDT(l.updatedAt) : '—'}</td>
                  <td style={{ ...P.td, textAlign: 'right' }}>
                    {canAct && sendable(api) && (
                      <button type="button" style={{ ...P.btnGhost, padding: '5px 12px', fontSize: 12, opacity: busy ? 0.6 : 1 }}
                        disabled={!!busy} onClick={() => onSend(api)}>
                        {busy === `ifms-${api}` ? 'Sending…' : l ? '⟳ Retry' : 'Send'}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Update Trip (same fields as the RFCL VLTS "Update Trip" pop-up) ─────────────
type UpdateKey = 'ddCode' | 'dealerCode' | 'waybillNumber' | 'invoiceNumber' | 'quantityMt' | 'destStateCode' | 'destDistrictCode' | 'dispatchType';
const UPDATE_FIELDS: { key: UpdateKey; label: string; placeholder: string; numeric?: boolean; options?: string[] }[] = [
  { key: 'ddCode', label: 'Dispatch Code (DD)', placeholder: 'e.g. 100158080430' },
  { key: 'dealerCode', label: 'Dealer Code', placeholder: 'e.g. 1642243' },
  { key: 'waybillNumber', label: 'Waybill Number', placeholder: 'e.g. 102557019455' },
  { key: 'invoiceNumber', label: 'Invoice Number', placeholder: 'e.g. 90003887' },
  { key: 'quantityMt', label: 'Quantity Dispatched (Metric Tons)', placeholder: 'e.g. 19.98', numeric: true },
  { key: 'destStateCode', label: 'Destination State Code', placeholder: 'e.g. 36' },
  { key: 'destDistrictCode', label: 'Destination District Code', placeholder: 'e.g. 2834' },
  { key: 'dispatchType', label: 'Dispatch Type', placeholder: '', options: ['Retailer', 'Dealer'] },
];

function UpdateTripModal({ trip, onClose, onSaved, onFullEdit, canFullEdit }: {
  trip: Trip; onClose: () => void; onSaved: () => void; onFullEdit: () => void; canFullEdit: boolean;
}) {
  const [form, setForm] = useState<Record<UpdateKey, string>>(() =>
    Object.fromEntries(UPDATE_FIELDS.map((f) => [f.key, trip[f.key] != null ? String(trip[f.key]) : (f.options ? f.options[0] : '')])) as Record<UpdateKey, string>);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);
  const set = (k: UpdateKey, v: string) => setForm((p) => ({ ...p, [k]: v }));

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setErr('');
    const blank = UPDATE_FIELDS.filter((f) => !form[f.key].trim()).map((f) => f.label);
    if (blank.length) { setErr(`Please fill: ${blank.join(', ')}. IFMS rejects trips with missing data.`); return; }
    const q = parseFloat(form.quantityMt);
    if (!Number.isFinite(q) || q <= 0) { setErr('Quantity must be a number above 0'); return; }
    setSaving(true);
    try { await updateTrip(trip.id, { ...form, quantityMt: q }); onSaved(); }
    catch (e2) { setErr(apiError(e2, 'Could not update the trip')); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Update Trip" subtitle={trip.tripNumber} onClose={onClose} width={680} footer={false}>
      <form onSubmit={save}>
        <div style={S.formGrid}>
          {UPDATE_FIELDS.map((f) => (
            <div key={f.key}>
              <label style={S.fLabel}>{f.label}</label>
              {f.options ? (
                <select style={S.fInput} value={form[f.key]} onChange={(e) => set(f.key, e.target.value)}>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input style={{ ...S.fInput, ...(!form[f.key].trim() ? { borderColor: 'var(--fx-orange)' } : {}) }}
                  value={form[f.key]} placeholder={f.placeholder} inputMode={f.numeric ? 'decimal' : undefined}
                  onChange={(e) => set(f.key, e.target.value)} />
              )}
            </div>
          ))}
        </div>
        {err && <div style={S.formErr}>{err}</div>}
        <div style={S.formFoot}>
          {canFullEdit && <button type="button" style={{ ...P.btnGhost, fontSize: 12 }} onClick={onFullEdit}>Edit all trip details →</button>}
          <span style={{ flex: 1 }} />
          <button type="button" style={S.btnCancel} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...S.btnUpdate, opacity: saving ? 0.7 : 1 }} disabled={saving}>{saving ? 'Updating…' : 'Update'}</button>
        </div>
      </form>
    </Modal>
  );
}

// ── Update Status ─────────────────────────────────────────────────────────────
// Allowed moves: Pending → On Going / Cancelled, On Going → Completed / Cancelled.
// On Going sends IFMS Trip Start; Completed sends Trip End + Closure.
type NextStatus = 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
const NEXT: Record<'PENDING' | 'ACTIVE', NextStatus[]> = { PENDING: ['ACTIVE', 'CANCELLED'], ACTIVE: ['COMPLETED', 'CANCELLED'] };
const NEXT_INFO: Record<NextStatus, string> = {
  ACTIVE: 'Starts live tracking and sends Trip Start to IFMS.',
  COMPLETED: 'Stops tracking, frees the vehicle and sends Trip End + Trip Closure to IFMS.',
  CANCELLED: 'Stops tracking and frees the vehicle. Nothing further is sent to IFMS. This cannot be undone.',
};

function StatusModal({ trip, onClose, onDone }: { trip: Trip; onClose: () => void; onDone: (text: string) => void }) {
  const options = NEXT[trip.status as 'PENDING' | 'ACTIVE'] || [];
  const [next, setNext] = useState<NextStatus>(options[0]);
  const [ack, setAck] = useState(trip.quantityMt != null ? String(trip.quantityMt) : '');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault(); setErr('');
    let ackNum: number | undefined;
    if (next === 'COMPLETED' && ack.trim()) {
      ackNum = parseFloat(ack);
      if (!Number.isFinite(ackNum) || ackNum < 0) { setErr('Acknowledged quantity must be a number'); return; }
      if (trip.quantityMt != null && ackNum > trip.quantityMt) { setErr(`Cannot be more than dispatched (${trip.quantityMt} MT)`); return; }
    }
    setSaving(true);
    try {
      if (next === 'ACTIVE') { await startTrip(trip.id); onDone('Trip is now On Going — IFMS Trip Start is being sent'); }
      else if (next === 'COMPLETED') { await completeTrip(trip.id, { ackQuantityMt: ackNum }); onDone('Trip completed — IFMS Trip End and Closure are being sent'); }
      else { await cancelTrip(trip.id, { reason: reason.trim() }); onDone('Trip cancelled'); }
    } catch (e2) {
      setErr(apiError(e2, 'Could not update the status'));
    } finally { setSaving(false); }
  };

  return (
    <Modal title="Update Status" subtitle={trip.tripNumber} onClose={onClose} width={520} footer={false}>
      <form onSubmit={save}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, fontSize: 13, color: 'var(--fx-text-2)' }}>
          Current status <span style={{ ...S.statusPill, padding: '4px 12px', fontSize: 12, background: STATUS[trip.status].bg }}>{STATUS[trip.status].label}</span>
        </div>
        <label style={S.fLabel}>New Status</label>
        <div style={{ display: 'grid', gap: 8, marginBottom: 14 }}>
          {options.map((o) => (
            <label key={o} style={{ ...S.choice, ...(next === o ? S.choiceOn : {}) }}>
              <input type="radio" name="status" checked={next === o} onChange={() => setNext(o)} style={{ marginTop: 3 }} />
              <span>
                <b style={{ display: 'block', fontSize: 13.5, color: o === 'CANCELLED' ? 'var(--fx-red)' : 'var(--fx-text)' }}>{STATUS[o].label}</b>
                <span style={{ fontSize: 12, color: 'var(--fx-text-2)' }}>{NEXT_INFO[o]}</span>
              </span>
            </label>
          ))}
        </div>
        {next === 'COMPLETED' && (
          <div style={{ marginBottom: 12 }}>
            <label style={S.fLabel}>Quantity Acknowledged (Metric Tons)</label>
            <input style={S.fInput} value={ack} inputMode="decimal" onChange={(e) => setAck(e.target.value)} placeholder="e.g. 19.98" />
            <div style={{ fontSize: 11.5, color: 'var(--fx-text-3)', marginTop: 4 }}>
              Received at the destination{trip.quantityMt != null ? ` · dispatched ${trip.quantityMt} MT` : ''}. Sent to IFMS in Trip End.
            </div>
          </div>
        )}
        {next === 'CANCELLED' && (
          <div style={{ marginBottom: 12 }}>
            <label style={S.fLabel}>Reason</label>
            <textarea style={{ ...S.fInput, height: 'auto', minHeight: 76, resize: 'vertical', padding: '10px 12px' }} value={reason} maxLength={500}
              onChange={(e) => setReason(e.target.value)} placeholder="e.g. Vehicle breakdown, order cancelled…" />
          </div>
        )}
        {err && <div style={S.formErr}>{err}</div>}
        <div style={S.formFoot}>
          <span style={{ flex: 1 }} />
          <button type="button" style={S.btnCancel} onClick={onClose}>Cancel</button>
          <button type="submit" style={{ ...S.btnUpdate, ...(next === 'CANCELLED' ? { background: 'var(--fx-red)' } : {}), opacity: saving ? 0.7 : 1 }} disabled={saving}>
            {saving ? 'Updating…' : 'Update'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function escapeHtml(s: string) {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c] as string));
}

const PAGE_CSS = `
  /* Leaflet gives every div icon a white box + border — our markers draw their own. */
  .fx-pin, .fx-truck, .fx-dev { background: none !important; border: none !important; }
  /* a 0-size div icon would squash the label, so let it size to its text */
  .fx-dev { width: max-content !important; height: auto !important; }
  .fx-pin-b { width: 28px; height: 28px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); display: flex; align-items: center; justify-content: center; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,.35); }
  .fx-pin-b span { transform: rotate(45deg); color: #fff; font: 700 12px -apple-system, system-ui, sans-serif; }
  .fx-dev-b { transform: translate(-50%, -150%); white-space: nowrap; background: #FF3B30; color: #fff; font: 700 11px -apple-system, system-ui, sans-serif; padding: 3px 9px; border-radius: 20px; box-shadow: 0 2px 6px rgba(0,0,0,.35); border: 2px solid #fff; }
  .fx-replay { background: none !important; border: none !important; }
  .fx-replay-b { width: 28px; height: 28px; border-radius: 50%; background: #fff; border: 2px solid #1DB954;
    display: flex; align-items: center; justify-content: center; font-size: 14px; box-shadow: 0 2px 6px rgba(0,0,0,.3); }
  .fx-truck-b { width: 40px; height: 40px; border-radius: 50%; background: #34A853; display: flex; align-items: center; justify-content: center; font-size: 18px; border: 3px solid #fff; box-shadow: 0 3px 10px rgba(0,0,0,.4); }
  .live-dot { width: 8px; height: 8px; border-radius: 50%; background: #34C759; animation: livePulse 1.3s ease-out infinite; flex: none; }
  @keyframes livePulse { 0% { box-shadow: 0 0 0 0 rgba(52,199,89,.55); } 70% { box-shadow: 0 0 0 7px rgba(52,199,89,0); } 100% { box-shadow: 0 0 0 0 rgba(52,199,89,0); } }
  @media (max-width: 700px) { .fx-drow { grid-template-columns: 120px 1fr !important; } }
  @media (prefers-reduced-motion: reduce) { .live-dot { animation: none; } }
`;

const S = css({
  panel: { background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14, boxShadow: 'var(--fx-shadow)', overflow: 'hidden' },
  panelHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '16px 18px', borderBottom: '1px solid var(--fx-border)' },
  panelTitle: { fontSize: 15, fontWeight: 700, color: 'var(--fx-text)' },
  panelSub: { fontSize: 12, color: 'var(--fx-text-2)', marginTop: 2 },
  statusPill: { color: '#fff', fontSize: 12.5, fontWeight: 600, padding: '8px 18px', borderRadius: 8, whiteSpace: 'nowrap' },
  kmBadge: { background: 'var(--fx-text)', color: 'var(--fx-surface)', fontSize: 12, fontWeight: 700, padding: '4px 9px', borderRadius: 6, whiteSpace: 'nowrap' },
  secHead: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 14px', fontSize: 12, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--fx-text)', background: 'var(--fx-surface-2)' },
  dLbl: { padding: '10px 14px', fontSize: 12.5, color: 'var(--fx-text-2)', background: 'var(--fx-surface-2)' },
  dVal: { padding: '10px 14px', fontSize: 13, color: 'var(--fx-text)', fontWeight: 500, wordBreak: 'break-word' },
  missing: { color: 'var(--fx-orange)', fontWeight: 600, fontSize: 12 },
  place: { display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.4 },
  dot: { width: 10, height: 10, borderRadius: '50%', flex: 'none', marginTop: 4, boxShadow: '0 0 0 3px var(--fx-surface-2)' },
  link: { color: 'var(--fx-accent)', textDecoration: 'none', fontWeight: 600 },
  showTruck: { position: 'absolute', top: 12, right: 12, zIndex: 500, padding: '7px 12px', borderRadius: 10, border: '1px solid var(--fx-border)', background: 'var(--fx-surface)', color: 'var(--fx-text)', fontSize: 12.5, fontWeight: 600, cursor: 'pointer', boxShadow: 'var(--fx-shadow-lg)', fontFamily: 'inherit' },
  replayInfo: { position: 'absolute', bottom: 12, left: 12, zIndex: 500, display: 'flex', alignItems: 'center', gap: 9, padding: '6px 12px', borderRadius: 9, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', color: 'var(--fx-text)', fontSize: 12, fontWeight: 600, boxShadow: 'var(--fx-shadow-lg)', whiteSpace: 'nowrap' },
  replayClock: { color: 'var(--fx-text)' },
  replaySep: { width: 1, height: 13, background: 'var(--fx-border-strong)' },
  replaySpeed: { color: 'var(--fx-green)', fontVariantNumeric: 'tabular-nums' },
  replayPaused: { fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.4, color: 'var(--fx-text-3)' },
  mapFoot: { display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '10px 16px', borderTop: '1px solid var(--fx-border)' },
  legend: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--fx-text-2)' },
  legendBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--fx-text-2)', background: 'transparent', border: 'none', padding: '2px 4px', fontFamily: 'inherit', transition: 'opacity .15s' },
  legLine: { width: 18, height: 4, borderRadius: 2, display: 'inline-block' },
  legDot: { width: 10, height: 10, borderRadius: '50%', display: 'inline-block' },
  progTrack: { flex: 1, height: 6, borderRadius: 4, background: 'var(--fx-gray-soft)', overflow: 'hidden', minWidth: 80 },
  progFill: { display: 'block', height: '100%', borderRadius: 4, background: 'linear-gradient(90deg,#F39C33,#34A853)', transition: 'width .6s' },
  tile: { background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 14, padding: '14px 16px', boxShadow: 'var(--fx-shadow)' },
  tileIcon: { width: 34, height: 34, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' },
  tileVal: { fontSize: 17, fontWeight: 700, color: 'var(--fx-text)', letterSpacing: '-0.01em', textAlign: 'right' },
  tileHint: { fontSize: 11.5, color: 'var(--fx-text-3)', marginTop: 8 },
  blockTitle: { fontSize: 15, fontWeight: 700, color: 'var(--fx-text)', margin: '0 2px 10px', display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  blockSub: { fontSize: 12, fontWeight: 400, color: 'var(--fx-text-2)' },
  code: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 11.5, color: 'var(--fx-text)', wordBreak: 'break-all', whiteSpace: 'pre-wrap' },
  ifmsNote: { fontSize: 12.5, color: 'var(--fx-text)', background: 'var(--fx-orange-soft)', padding: '10px 18px', borderBottom: '1px solid var(--fx-border)' },
  warn: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--fx-text)', background: 'var(--fx-orange-soft)', border: '1px solid var(--fx-border)', borderRadius: 10, padding: '9px 14px', marginBottom: 14 },
  warnBtn: { marginLeft: 'auto', background: 'var(--fx-orange)', color: '#fff', border: 'none', borderRadius: 8, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  menu: { position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, minWidth: 190, background: 'var(--fx-surface)', border: '1px solid var(--fx-border)', borderRadius: 12, boxShadow: 'var(--fx-shadow-lg)', padding: 6 },
  menuItem: { display: 'block', width: '100%', textAlign: 'left', padding: '9px 12px', border: 'none', background: 'transparent', borderRadius: 8, fontSize: 13, fontWeight: 500, color: 'var(--fx-text)', cursor: 'pointer', fontFamily: 'inherit' },
  notice: { display: 'flex', alignItems: 'center', gap: 9, fontSize: 12.5, padding: '9px 12px', borderRadius: 10, marginBottom: 14, border: '1px solid var(--fx-border)' },
  noticeOk: { background: 'var(--fx-green-soft)', color: 'var(--fx-green)' },
  noticeErr: { background: 'var(--fx-red-soft)', color: 'var(--fx-red)' },
  rowBtn: { background: 'var(--fx-gray-soft)', border: 'none', color: 'var(--fx-accent)', width: 28, height: 28, borderRadius: 7, cursor: 'pointer', marginLeft: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' },
  formGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(250px,1fr))', gap: '14px 18px' },
  fLabel: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--fx-text)', marginBottom: 6 },
  fInput: { width: '100%', height: 42, padding: '0 12px', borderRadius: 10, border: '1px solid var(--fx-border-strong)', background: 'var(--fx-input)', color: 'var(--fx-text)', fontSize: 14, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' },
  formErr: { color: 'var(--fx-red)', fontSize: 12.5, marginTop: 12 },
  formFoot: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 18, paddingTop: 14, borderTop: '1px solid var(--fx-border)' },
  btnCancel: { background: '#E5646E', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  btnUpdate: { background: '#1F4E9C', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' },
  choice: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--fx-border-strong)', cursor: 'pointer', background: 'var(--fx-input)' },
  choiceOn: { borderColor: 'var(--fx-accent)', background: 'var(--fx-accent-soft)' },
  noticeX: { background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 12, opacity: 0.7 },
});
